import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  HStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Switch,
  useToast,
  Spinner,
  Center,
  Badge,
  Flex,
  Text,
  Checkbox,
  CheckboxGroup,
  SimpleGrid,
  Textarea,
  Code,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Alert,
  AlertIcon,
  VStack,
} from '@chakra-ui/react';
import { FaEdit, FaTrash, FaPlus, FaPlay, FaHistory, FaShieldAlt } from 'react-icons/fa';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const Webhooks = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    secret: '',
    events: 'all',
    is_active: true,
    verify_ssl: true,
  });
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  
  // Signature verification state
  const [verifyPayload, setVerifyPayload] = useState('{"event": "test", "data": {}}');
  const [verifySecret, setVerifySecret] = useState('');
  const [verifySignature, setVerifySignature] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isTestOpen, onOpen: onTestOpen, onClose: onTestClose } = useDisclosure();
  const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
  const { isOpen: isVerifyOpen, onOpen: onVerifyOpen, onClose: onVerifyClose } = useDisclosure();
  const toast = useToast();

  const eventTypes = [
    { value: 'instance.created', label: 'Instance Created', description: 'When a new instance is created' },
    { value: 'instance.updated', label: 'Instance Updated', description: 'When an instance is modified' },
    { value: 'instance.deleted', label: 'Instance Deleted', description: 'When an instance is removed' },
  ];

  // Convert events string to array for checkboxes
  const getEventsArray = (eventsStr) => {
    if (!eventsStr || eventsStr === 'all') {
      return ['instance.created', 'instance.updated', 'instance.deleted'];
    }
    return eventsStr.split(',').filter(e => e);
  };

  // Convert events array to string for storage
  const getEventsString = (eventsArr) => {
    if (eventsArr.length === 3) {
      return 'all';
    }
    return eventsArr.join(',');
  };

  const handleEventChange = (values) => {
    setFormData(prev => ({
      ...prev,
      events: getEventsString(values)
    }));
  };

  const fetchWebhooks = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/webhook-configs/', {
        headers: {
          'Authorization': `Token ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data);
      }
    } catch (error) {
      toast({
        title: 'Error fetching webhooks',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const handleAdd = () => {
    setSelectedWebhook(null);
    setFormData({
      name: '',
      url: '',
      secret: '',
      events: 'all',
      is_active: true,
      verify_ssl: true,
    });
    onOpen();
  };

  const handleEdit = (webhook) => {
    setSelectedWebhook(webhook);
    setFormData({
      name: webhook.name,
      url: webhook.url,
      secret: '',  // Don't show existing secret
      events: webhook.events,
      is_active: webhook.is_active,
      verify_ssl: webhook.verify_ssl,
    });
    onOpen();
  };

  const handleDelete = async (webhook) => {
    if (!window.confirm(`Are you sure you want to delete the webhook "${webhook.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/webhook-configs/${webhook.id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${localStorage.getItem('token')}`,
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });

      if (response.ok) {
        toast({
          title: 'Webhook deleted',
          status: 'success',
          duration: 3000,
        });
        fetchWebhooks();
      }
    } catch (error) {
      toast({
        title: 'Error deleting webhook',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleSave = async () => {
    const url = selectedWebhook
      ? `/api/v1/webhook-configs/${selectedWebhook.id}/`
      : '/api/v1/webhook-configs/';

    const method = selectedWebhook ? 'PUT' : 'POST';

    // Don't send empty secret on update (keep existing)
    const payload = { ...formData };
    if (selectedWebhook && !payload.secret) {
      delete payload.secret;
    }

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('token')}`,
          'X-CSRFToken': getCookie('csrftoken'),
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast({
          title: selectedWebhook ? 'Webhook updated' : 'Webhook created',
          status: 'success',
          duration: 3000,
        });
        onClose();
        fetchWebhooks();
      } else {
        const error = await response.json();
        toast({
          title: 'Error saving webhook',
          description: JSON.stringify(error),
          status: 'error',
          duration: 5000,
        });
      }
    } catch (error) {
      toast({
        title: 'Error saving webhook',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleTest = async (webhook) => {
    setSelectedWebhook(webhook);
    setTestResult(null);
    setTestLoading(true);
    onTestOpen();

    try {
      const response = await fetch(`/api/v1/webhook-configs/${webhook.id}/test/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${localStorage.getItem('token')}`,
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });

      if (response.ok) {
        const result = await response.json();
        setTestResult(result);
      }
    } catch (error) {
      setTestResult({
        success: false,
        error_message: 'Failed to send test webhook',
      });
    } finally {
      setTestLoading(false);
    }
  };

  const handleViewHistory = async (webhook) => {
    setSelectedWebhook(webhook);
    setDeliveriesLoading(true);
    setDeliveries([]);
    onHistoryOpen();

    try {
      const response = await fetch(`/api/v1/webhook-configs/${webhook.id}/deliveries/`, {
        headers: {
          'Authorization': `Token ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDeliveries(data);
      }
    } catch (error) {
      toast({
        title: 'Error fetching delivery history',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setDeliveriesLoading(false);
    }
  };

  const handleVerifySignature = async () => {
    setVerifyLoading(true);
    setVerifyResult(null);

    try {
      let payload;
      try {
        payload = JSON.parse(verifyPayload);
      } catch {
        setVerifyResult({ valid: false, error: 'Invalid JSON payload' });
        setVerifyLoading(false);
        return;
      }

      const response = await fetch('/api/v1/webhook-configs/verify_signature/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('token')}`,
          'X-CSRFToken': getCookie('csrftoken'),
        },
        body: JSON.stringify({
          payload,
          secret: verifySecret,
          signature: verifySignature,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setVerifyResult(result);
      }
    } catch (error) {
      setVerifyResult({ valid: false, error: 'Verification request failed' });
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'green';
      case 'failed':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <Center h="200px">
        <Spinner size="xl" color="purple.500" thickness="4px" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="gray.800">Webhooks</Heading>
          <Text color="gray.500" mt={1}>Configure webhooks to receive notifications when instances change</Text>
        </Box>
        <HStack spacing={3}>
          <Button
            leftIcon={<FaShieldAlt />}
            variant="outline"
            colorScheme="purple"
            onClick={onVerifyOpen}
            borderRadius="lg"
          >
            Verify Signature
          </Button>
          <Button
            leftIcon={<FaPlus />}
            bgGradient="linear(to-r, purple.500, purple.600)"
            color="white"
            _hover={{
              bgGradient: "linear(to-r, purple.600, purple.700)",
              transform: "translateY(-2px)",
              boxShadow: "lg",
            }}
            onClick={handleAdd}
            borderRadius="lg"
            px={6}
          >
            Add Webhook
          </Button>
        </HStack>
      </Flex>

      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        borderWidth="1px"
        borderColor="gray.100"
        overflow="hidden"
      >
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th color="gray.600" py={4}>Name</Th>
              <Th color="gray.600">URL</Th>
              <Th color="gray.600">Events</Th>
              <Th color="gray.600">Status</Th>
              <Th color="gray.600" textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {webhooks.length === 0 ? (
              <Tr>
                <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                  No webhooks configured. Click "Add Webhook" to create one.
                </Td>
              </Tr>
            ) : (
              webhooks.map((webhook) => (
                <Tr
                  key={webhook.id}
                  _hover={{ bg: "gray.50" }}
                  transition="background 0.2s"
                >
                  <Td fontWeight="medium" color="gray.800">{webhook.name}</Td>
                  <Td color="gray.600" maxW="250px" isTruncated>{webhook.url}</Td>
                  <Td>
                    <Badge
                      colorScheme="purple"
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {webhook.events === 'all' ? 'All Events' : webhook.events.split(',').length + ' events'}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={webhook.is_active ? 'green' : 'gray'}
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {webhook.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td textAlign="right">
                    <HStack spacing={2} justify="flex-end">
                      <IconButton
                        aria-label="Test webhook"
                        icon={<FaPlay />}
                        size="sm"
                        variant="ghost"
                        colorScheme="green"
                        onClick={() => handleTest(webhook)}
                      />
                      <IconButton
                        aria-label="View history"
                        icon={<FaHistory />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => handleViewHistory(webhook)}
                      />
                      <IconButton
                        aria-label="Edit webhook"
                        icon={<FaEdit />}
                        size="sm"
                        variant="ghost"
                        colorScheme="purple"
                        onClick={() => handleEdit(webhook)}
                      />
                      <IconButton
                        aria-label="Delete webhook"
                        icon={<FaTrash />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(webhook)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            {selectedWebhook ? 'Edit' : 'Add New'} Webhook
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <FormControl isRequired>
              <FormLabel fontWeight="medium">Name</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Slack Notifications"
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel fontWeight="medium">URL</FormLabel>
              <Input
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                placeholder="https://example.com/webhook"
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                For Selwyn API: <Code fontSize="xs">https://api.selwynevents.com/api/v1/webhooks/license-manager/</Code>
              </Text>
            </FormControl>
            <FormControl mt={4}>
              <FormLabel fontWeight="medium">
                Secret {selectedWebhook && <Text as="span" color="gray.500" fontWeight="normal">(leave blank to keep current)</Text>}
              </FormLabel>
              <Input
                name="secret"
                type="password"
                value={formData.secret}
                onChange={handleInputChange}
                placeholder="Optional secret for signing payloads"
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                If set, payloads will be signed with HMAC-SHA256 and sent in X-Webhook-Signature header
              </Text>
            </FormControl>
            <FormControl mt={4}>
              <FormLabel fontWeight="medium">Events</FormLabel>
              <Text fontSize="sm" color="gray.500" mb={3}>
                Select which events should trigger this webhook
              </Text>
              <CheckboxGroup
                colorScheme="purple"
                value={getEventsArray(formData.events)}
                onChange={handleEventChange}
              >
                <SimpleGrid columns={1} spacing={3}>
                  {eventTypes.map((event) => (
                    <Box
                      key={event.value}
                      p={3}
                      borderWidth="1px"
                      borderRadius="lg"
                      borderColor={getEventsArray(formData.events).includes(event.value) ? 'purple.300' : 'gray.200'}
                      bg={getEventsArray(formData.events).includes(event.value) ? 'purple.50' : 'white'}
                      transition="all 0.2s"
                      _hover={{ borderColor: 'purple.300' }}
                    >
                      <Checkbox value={event.value}>
                        <Box ml={2}>
                          <Text fontWeight="medium" fontSize="sm">{event.label}</Text>
                          <Text fontSize="xs" color="gray.500">{event.description}</Text>
                        </Box>
                      </Checkbox>
                    </Box>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
              {getEventsArray(formData.events).length === 0 && (
                <Text fontSize="sm" color="red.500" mt={2}>
                  Please select at least one event
                </Text>
              )}
            </FormControl>
            <Flex mt={6} gap={8}>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" fontWeight="medium">Active</FormLabel>
                <Switch
                  name="is_active"
                  isChecked={formData.is_active}
                  onChange={handleInputChange}
                  colorScheme="purple"
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" fontWeight="medium">Verify SSL</FormLabel>
                <Switch
                  name="verify_ssl"
                  isChecked={formData.verify_ssl}
                  onChange={handleInputChange}
                  colorScheme="purple"
                />
              </FormControl>
            </Flex>
          </ModalBody>

          <ModalFooter borderTopWidth="1px" py={4}>
            <Button variant="ghost" mr={3} onClick={onClose} borderRadius="lg">
              Cancel
            </Button>
            <Button
              bgGradient="linear(to-r, purple.500, purple.600)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, purple.600, purple.700)",
              }}
              onClick={handleSave}
              borderRadius="lg"
              px={6}
            >
              {selectedWebhook ? 'Update' : 'Create'} Webhook
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Test Result Modal */}
      <Modal isOpen={isTestOpen} onClose={onTestClose} size="lg">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            Test Webhook: {selectedWebhook?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            {testLoading ? (
              <Center py={8}>
                <VStack spacing={4}>
                  <Spinner size="xl" color="purple.500" thickness="4px" />
                  <Text color="gray.500">Sending test webhook...</Text>
                </VStack>
              </Center>
            ) : testResult ? (
              <VStack spacing={4} align="stretch">
                <Alert
                  status={testResult.success ? 'success' : 'error'}
                  borderRadius="lg"
                >
                  <AlertIcon />
                  {testResult.success ? 'Webhook delivered successfully!' : 'Webhook delivery failed'}
                </Alert>
                
                {testResult.response_status && (
                  <Box>
                    <Text fontWeight="medium" mb={2}>Response Status</Text>
                    <Badge
                      colorScheme={testResult.response_status >= 200 && testResult.response_status < 300 ? 'green' : 'red'}
                      fontSize="md"
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      {testResult.response_status}
                    </Badge>
                  </Box>
                )}
                
                {testResult.error_message && (
                  <Box>
                    <Text fontWeight="medium" mb={2}>Error</Text>
                    <Code
                      display="block"
                      p={3}
                      borderRadius="md"
                      bg="red.50"
                      color="red.600"
                    >
                      {testResult.error_message}
                    </Code>
                  </Box>
                )}
                
                {testResult.response_body && (
                  <Box>
                    <Text fontWeight="medium" mb={2}>Response Body</Text>
                    <Code
                      display="block"
                      p={3}
                      borderRadius="md"
                      bg="gray.800"
                      color="green.300"
                      fontSize="xs"
                      whiteSpace="pre-wrap"
                      maxH="200px"
                      overflowY="auto"
                    >
                      {testResult.response_body}
                    </Code>
                  </Box>
                )}
              </VStack>
            ) : null}
          </ModalBody>

          <ModalFooter borderTopWidth="1px" py={4}>
            <Button onClick={onTestClose} borderRadius="lg">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delivery History Modal */}
      <Modal isOpen={isHistoryOpen} onClose={onHistoryClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl" maxW="800px">
          <ModalHeader borderBottomWidth="1px" py={4}>
            Delivery History: {selectedWebhook?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            {deliveriesLoading ? (
              <Center py={8}>
                <Spinner size="xl" color="purple.500" thickness="4px" />
              </Center>
            ) : deliveries.length === 0 ? (
              <Center py={8}>
                <Text color="gray.500">No delivery history yet</Text>
              </Center>
            ) : (
              <Accordion allowMultiple>
                {deliveries.map((delivery) => (
                  <AccordionItem key={delivery.id} border="none" mb={2}>
                    <AccordionButton
                      bg="gray.50"
                      borderRadius="lg"
                      _hover={{ bg: 'gray.100' }}
                      _expanded={{ bg: 'gray.100', borderBottomRadius: 0 }}
                    >
                      <HStack flex="1" textAlign="left" spacing={4}>
                        <Badge
                          colorScheme={getStatusColor(delivery.status)}
                          borderRadius="full"
                          px={3}
                          py={1}
                        >
                          {delivery.status}
                        </Badge>
                        <Text fontWeight="medium">{delivery.event}</Text>
                        <Text fontSize="sm" color="gray.500">
                          {new Date(delivery.created_at).toLocaleString()}
                        </Text>
                        {delivery.response_status && (
                          <Badge colorScheme="gray">{delivery.response_status}</Badge>
                        )}
                      </HStack>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4} bg="white" borderWidth="1px" borderTop="none" borderBottomRadius="lg">
                      <VStack align="stretch" spacing={4}>
                        <Box>
                          <Text fontWeight="medium" mb={2}>Payload</Text>
                          <Code
                            display="block"
                            p={3}
                            borderRadius="md"
                            bg="gray.800"
                            color="green.300"
                            fontSize="xs"
                            whiteSpace="pre-wrap"
                            maxH="150px"
                            overflowY="auto"
                          >
                            {JSON.stringify(delivery.payload, null, 2)}
                          </Code>
                        </Box>
                        {delivery.error_message && (
                          <Box>
                            <Text fontWeight="medium" mb={2}>Error</Text>
                            <Code
                              display="block"
                              p={3}
                              borderRadius="md"
                              bg="red.50"
                              color="red.600"
                            >
                              {delivery.error_message}
                            </Code>
                          </Box>
                        )}
                        {delivery.response_body && (
                          <Box>
                            <Text fontWeight="medium" mb={2}>Response</Text>
                            <Code
                              display="block"
                              p={3}
                              borderRadius="md"
                              bg="gray.100"
                              fontSize="xs"
                              whiteSpace="pre-wrap"
                              maxH="100px"
                              overflowY="auto"
                            >
                              {delivery.response_body}
                            </Code>
                          </Box>
                        )}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </ModalBody>

          <ModalFooter borderTopWidth="1px" py={4}>
            <Button onClick={onHistoryClose} borderRadius="lg">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Verify Signature Modal */}
      <Modal isOpen={isVerifyOpen} onClose={onVerifyClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            Verify Webhook Signature
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <Text color="gray.600" mb={4}>
              Use this tool to verify that a webhook signature matches the expected value.
              This helps debug webhook integration issues.
            </Text>
            
            <FormControl>
              <FormLabel fontWeight="medium">Payload (JSON)</FormLabel>
              <Textarea
                value={verifyPayload}
                onChange={(e) => setVerifyPayload(e.target.value)}
                placeholder='{"event": "test", "data": {}}'
                borderRadius="lg"
                focusBorderColor="purple.500"
                fontFamily="mono"
                fontSize="sm"
                rows={4}
              />
            </FormControl>
            
            <FormControl mt={4}>
              <FormLabel fontWeight="medium">Secret</FormLabel>
              <Input
                value={verifySecret}
                onChange={(e) => setVerifySecret(e.target.value)}
                placeholder="Your webhook secret"
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
            </FormControl>
            
            <FormControl mt={4}>
              <FormLabel fontWeight="medium">Signature to Verify</FormLabel>
              <Input
                value={verifySignature}
                onChange={(e) => setVerifySignature(e.target.value)}
                placeholder="sha256=..."
                borderRadius="lg"
                focusBorderColor="purple.500"
                fontFamily="mono"
              />
            </FormControl>
            
            {verifyResult && (
              <Alert
                status={verifyResult.valid ? 'success' : 'error'}
                borderRadius="lg"
                mt={4}
              >
                <AlertIcon />
                {verifyResult.valid
                  ? 'Signature is valid!'
                  : verifyResult.error || 'Signature is invalid'}
              </Alert>
            )}
          </ModalBody>

          <ModalFooter borderTopWidth="1px" py={4}>
            <Button variant="ghost" mr={3} onClick={onVerifyClose} borderRadius="lg">
              Cancel
            </Button>
            <Button
              bgGradient="linear(to-r, purple.500, purple.600)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, purple.600, purple.700)",
              }}
              onClick={handleVerifySignature}
              isLoading={verifyLoading}
              borderRadius="lg"
              px={6}
            >
              Verify Signature
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Webhooks;
